// src/pages/business/inventory/InventoryManagementPage.tsx
// Mimics the mobile InventoryManagementScreen with enhanced web functionality

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Package,
  TrendingUp,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronUp,
  Tag,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface ProductStats {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  category: string;
  price: number;
  cost_price: number;
  quantity_in_stock: number;
  low_stock_threshold: number;
  is_active: boolean;
  stock_status: 'low' | 'good';
  total_sold: number;
  total_revenue: number;
  total_profit: number;
  sales_count: number;
  last_sale_date: string | null;
  sales_history: Array<{
    date: string;
    quantity: number;
    revenue: number;
  }>;
  created_at: string;
  updated_at: string;
  unit_type?: string | null;
  bulk_unit_size?: number | null;
  sale_prices?: Array<{
    id: string;
    unit_quantity: number;
    unit_price: number;
  }>;
}

interface InventorySummary {
  total_products: number;
  active_products: number;
  low_stock_products: number;
  out_of_stock_products: number;
  total_inventory_value: number;
  total_retail_value: number;
}

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 'KES 0.00';
  }
  return `KES ${amount.toLocaleString('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ── Summary Card Component ─────────────────────────────────────────────────────
const SummaryCard: React.FC<{
  summary: InventorySummary;
}> = ({ summary }) => {
  const potentialProfit = summary.total_retail_value - summary.total_inventory_value;

  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 mb-5 shadow-sm border border-light-border dark:border-dark-border">
      {/* Top Row - Product Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-primary dark:text-golden">{summary.total_products}</p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Total Products</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-emerald-500">{summary.active_products}</p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Active</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-500">{summary.low_stock_products}</p>
          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Low Stock</p>
        </div>
      </div>

      <div className="border-t border-light-border dark:border-dark-border my-4" />

      {/* Inventory Value Card */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Package size={18} className="text-primary" />
          <p className="text-sm text-light-subtle dark:text-dark-subtle font-medium">Inventory Value (Cost)</p>
        </div>
        <p className="text-2xl font-bold text-light-text dark:text-dark-text ml-7">
          {formatCurrency(summary.total_inventory_value)}
        </p>
      </div>

      {/* Retail Value Card */}
      <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Tag size={18} className="text-emerald-500" />
          <p className="text-sm text-light-subtle dark:text-dark-subtle font-medium">Retail Value (Selling Price)</p>
        </div>
        <p className="text-2xl font-bold text-light-text dark:text-dark-text ml-7">
          {formatCurrency(summary.total_retail_value)}
        </p>
        <div className="flex items-center gap-2 mt-2 ml-7">
          <TrendingUp size={14} className="text-emerald-500" />
          <p className="text-sm font-semibold text-emerald-500">
            Potential Profit: {formatCurrency(potentialProfit)}
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Product Card Component ─────────────────────────────────────────────────────
const ProductCard: React.FC<{
  item: ProductStats;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ item, isExpanded, onToggle }) => {
  const stockColor = item.stock_status === 'low' ? '#FF6B6B' : '#51CF66';
  const statusColor = item.is_active ? '#51CF66' : '#FF6B6B';

  return (
    <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 mb-3 shadow-sm border border-light-border dark:border-dark-border">
      {/* Product Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between mb-3"
      >
        <div className="flex-1 text-left">
          <p className="font-bold text-light-text dark:text-dark-text" style={{ fontSize: 16 }}>
            {item.name}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-light-subtle dark:text-dark-subtle">{item.category}</span>
            {item.sku && (
              <>
                <span className="text-light-subtle dark:text-dark-subtle">•</span>
                <span className="text-sm text-light-subtle dark:text-dark-subtle">SKU: {item.sku}</span>
              </>
            )}
            {item.unit_type && (
              <span
                className="px-2 py-0.5 rounded text-xs font-semibold"
                style={{ backgroundColor: '#4F46E520', color: '#4F46E5' }}
              >
                Bulk - {item.unit_type}
              </span>
            )}
          </div>
        </div>
        {isExpanded ? <ChevronUp size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
      </button>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="text-center">
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Stock</p>
          <p className="font-semibold" style={{ fontSize: 14, color: stockColor }}>{item.quantity_in_stock}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Price</p>
          <p className="font-semibold" style={{ fontSize: 14, color: '#0F172A' }}>{formatCurrency(item.price)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Sold</p>
          <p className="font-semibold" style={{ fontSize: 14, color: '#4F46E5' }}>{item.total_sold}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-light-subtle dark:text-dark-subtle">Revenue</p>
          <p className="font-semibold" style={{ fontSize: 14, color: '#51CF66' }}>{formatCurrency(item.total_revenue)}</p>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-light-border dark:border-dark-border">
          {/* Detailed Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Cost Price</p>
              <p className="font-semibold text-light-text dark:text-dark-text">{formatCurrency(item.cost_price)}</p>
            </div>
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Total Profit</p>
              <p className="font-semibold" style={{ color: '#51CF66' }}>{formatCurrency(item.total_profit)}</p>
            </div>
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Sales Count</p>
              <p className="font-semibold text-light-text dark:text-dark-text">{item.sales_count}</p>
            </div>
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Last Sale</p>
              <p className="font-semibold text-light-text dark:text-dark-text">{formatDate(item.last_sale_date)}</p>
            </div>
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Low Stock Alert</p>
              <p className="font-semibold text-light-text dark:text-dark-text">{item.low_stock_threshold}</p>
            </div>
            <div>
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Status</p>
              <p className="font-semibold" style={{ color: statusColor }}>{item.is_active ? 'Active' : 'Inactive'}</p>
            </div>
          </div>

          {/* Bulk Prices Section */}
          {item.unit_type && item.sale_prices && item.sale_prices.length > 0 && (
            <>
              <div className="border-t border-light-border dark:border-dark-border my-3" />
              <p className="font-semibold text-light-text dark:text-dark-text mb-2" style={{ fontSize: 14 }}>
                Configured Unit Prices
              </p>
              <div className="flex flex-wrap gap-2">
                {item.sale_prices.map((salePrice) => (
                  <div
                    key={salePrice.id}
                    className="px-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg"
                  >
                    <p className="text-sm font-semibold text-primary" style={{ fontSize: 12 }}>
                      {salePrice.unit_quantity} {item.unit_type}
                    </p>
                    <p className="font-bold" style={{ color: '#51CF66', fontSize: 14 }}>
                      {formatCurrency(salePrice.unit_price)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Sales History */}
          {item.sales_history && item.sales_history.length > 0 && (
            <>
              <div className="border-t border-light-border dark:border-dark-border my-3" />
              <p className="font-semibold text-light-text dark:text-dark-text mb-2" style={{ fontSize: 14 }}>
                Recent Sales History
              </p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {item.sales_history.slice(0, 10).map((sale, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 rounded-lg bg-light-bg dark:bg-dark-bg flex-shrink-0"
                    style={{ minWidth: 120 }}
                  >
                    <p className="text-sm text-light-text dark:text-dark-text">{formatDate(sale.date)}</p>
                    <p className="text-sm font-semibold text-primary" style={{ fontSize: 12 }}>
                      Qty: {sale.quantity}
                    </p>
                    <p className="font-bold" style={{ color: '#51CF66', fontSize: 12 }}>
                      {formatCurrency(sale.revenue)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Page Component ────────────────────────────────────────────────────────
const InventoryManagementPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [products, setProducts] = useState<ProductStats[]>([]);
  const [summary, setSummary] = useState<InventorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  const fetchInventoryStats = useCallback(async () => {
    try {
      const res = await apiClient.get(`/businesses/products/${businessId}/inventory-stats`);
      setProducts(res.data.products || []);
      setSummary(res.data.summary || null);
    } catch (error: any) {
      console.error('Fetch inventory stats error:', error);
      toastError('Failed to fetch inventory data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [businessId, toastError]);

  useEffect(() => {
    fetchInventoryStats();
  }, [fetchInventoryStats]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchInventoryStats();
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      // Simple export - opens the export endpoint in a new tab
      // The backend will handle authentication via cookies/session
      const exportUrl = `/api/businesses/products/${businessId}/export-inventory`;
      window.open(exportUrl, '_blank');
    } catch (error) {
      console.error('Export error:', error);
      toastError('Failed to export inventory');
    } finally {
      setExporting(false);
    }
  };

  const toggleProductExpansion = (productId: string) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const headerRight = (
    <button
      onClick={handleExportToExcel}
      disabled={exporting}
      className="p-2"
      title="Export to Excel"
    >
      {exporting ? (
        <Loader2 size={20} className="animate-spin text-primary" />
      ) : (
        <Download size={20} className="text-primary" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Inventory Management
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 size={40} className="animate-spin mx-auto text-primary" />
            <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading inventory...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Inventory Management
        </h1>
        {headerRight}
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Summary Card */}
        {summary && <SummaryCard summary={summary} />}

        {/* Products List */}
        {products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Package size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 16 }}>
              No products found
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              Add products to start tracking inventory
            </p>
          </div>
        ) : (
          products.map((product) => (
            <ProductCard
              key={product.id}
              item={product}
              isExpanded={expandedProduct === product.id}
              onToggle={() => toggleProductExpansion(product.id)}
            />
          ))
        )}
      </div>

      {/* Refresh Button - Fixed at bottom */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <RefreshCw size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};

export default InventoryManagementPage;